import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Leaf, CheckCircle2, AlertCircle, Send, Calendar, Users, MessageSquare } from 'lucide-react';
import { LOGO_URL } from '../mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import Footer from '../components/Footer';
import { CalendlyBooking } from '../components/CalendlyBooking';
import SEO from '../seo/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRe = /^[0-9+()\-\s]{7,20}$/;
const todayStr = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  name: '', company: '', email: '', phone: '',
  event_date: '', guest_count: '', message: '',
};

export default function CateringInquiry() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    document.title = 'Catering Inquiry — Wholesome 360';
    window.scrollTo(0, 0);
  }, []);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!emailRe.test(form.email.trim())) e.email = 'Valid email required';
    if (!phoneRe.test(form.phone.trim())) e.phone = 'Valid phone required';
    if (!form.event_date) e.event_date = 'Event date required';
    else if (form.event_date < todayStr()) e.event_date = 'Date must be in the future';
    if (!form.guest_count || Number(form.guest_count) < 1) e.guest_count = 'Guest count required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        company: form.company.trim() || null,
        email: form.email.trim(),
        phone: form.phone.trim(),
        event_date: form.event_date,
        guest_count: Number(form.guest_count),
        message: form.message.trim() || null,
      };
      const { data } = await axios.post(`${API}/catering-inquiry`, payload, { timeout: 15000 });
      setDone(data);
      setForm(EMPTY);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Something went wrong';
      setErrors({ _submit: typeof msg === 'string' ? msg : 'Submission failed. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="font-body bg-[#F6F2E8] min-h-screen flex flex-col">
      <SEO
        title="Plant-Based & Corporate Catering Chicago — Request a Quote"
        description="Book plant-based catering in Chicago: custom grain bowls catering, healthy salad wraps and Mediterranean spreads for offices and events. Reply within 1 business day."
        path="/catering-inquiry"
      />
      <header className="border-b border-[#D6CFB8] bg-[#F6F2E8]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Wholesome 360 logo" className="h-10 w-auto" />
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#245A40] hover:text-[#1B4631]">
            <ArrowLeft className="w-4 h-4" /> Back home
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-14 sm:py-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E6EBD5] text-[#1B4631] text-xs font-semibold border border-[#CFDAB1]">
              <Leaf className="w-3.5 h-3.5" /> Catering Inquiry
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[56px] text-[#1B2A22] mt-4 leading-[1.05]">
              Let’s cater <em className="italic text-[#245A40]">your event.</em>
            </h1>
            <p className="mt-5 text-[#3F4A42] text-[17px] leading-relaxed">
              Corporate lunches, weddings, holiday parties, or a one-time office spread — tell us the details and we’ll get back to you within one business day with a proposal tailored to your headcount and dietary needs.
            </p>

            <ul className="mt-8 space-y-3 text-sm text-[#3F4A42]">
              <InfoLine icon={Calendar} k="Lead time" v="24-hour notice minimum" />
              <InfoLine icon={Users} k="Minimum order" v="$300 · Free delivery included" />
              <InfoLine icon={MessageSquare} k="Response time" v="Within 1 business day" />
            </ul>

            <div className="mt-10 hidden lg:block bg-[#FBF8EF] rounded-2xl p-5 border border-[#D6CFB8]">
              <div className="text-[10px] uppercase tracking-widest text-[#245A40] font-bold">Prefer to call?</div>
              <div className="mt-2 font-display text-2xl text-[#1B2A22]">
                <a href="tel:7738570230" className="hover:text-[#245A40]">773-857-0230</a>
              </div>
              <div className="text-sm text-[#6F6A55] mt-1">Mon–Sun · 9 AM – 6 PM</div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <ThankYou inquiry={done} onNew={() => setDone(null)} />
            ) : (
              <form onSubmit={submit} className="bg-[#FBF8EF] rounded-3xl border border-[#D6CFB8] p-6 sm:p-8 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.12)] space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Name*" error={errors.name}>
                    <Input value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Full name" autoComplete="name" />
                  </Field>
                  <Field label="Company" hint="(optional)">
                    <Input value={form.company} onChange={(e) => setField('company', e.target.value)} placeholder="Company name" autoComplete="organization" />
                  </Field>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Email*" error={errors.email}>
                    <Input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="you@example.com" autoComplete="email" />
                  </Field>
                  <Field label="Phone*" error={errors.phone}>
                    <Input type="tel" value={form.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="773-555-0123" autoComplete="tel" />
                  </Field>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Event Date*" error={errors.event_date}>
                    <Input type="date" min={todayStr()} value={form.event_date} onChange={(e) => setField('event_date', e.target.value)} />
                  </Field>
                  <Field label="Guest Count*" error={errors.guest_count}>
                    <Input type="number" min="1" value={form.guest_count} onChange={(e) => setField('guest_count', e.target.value)} placeholder="e.g. 30" />
                  </Field>
                </div>

                <Field label="Special requests" hint="(optional)">
                  <Textarea rows={5} value={form.message} onChange={(e) => setField('message', e.target.value)} placeholder="Dietary restrictions, delivery details, budget, menu preferences, or anything else we should know…" />
                </Field>

                {errors._submit && (
                  <div className="text-sm px-3 py-2 rounded-lg bg-[#FCEEDB] text-[#9A3A0A] flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5" />
                    <span>{errors._submit}</span>
                  </div>
                )}

                <Button type="submit" disabled={submitting} className="btn-primary w-full h-12 rounded-full font-semibold text-base disabled:opacity-60">
                  {submitting ? 'Sending…' : (<><Send className="w-4 h-4 mr-1.5" /> Send my inquiry</>)}
                </Button>

                <p className="text-[11px] text-[#6F6A55] text-center">
                  By submitting you agree to be contacted about your event. We never share your info. See our <Link to="/privacy" className="underline hover:text-[#245A40]">Privacy Policy</Link>.
                </p>
              </form>
            )}
          </div>
        </div>

        <CalendlyBooking />
      </main>

      <Footer />
    </div>
  );
}

function ThankYou({ inquiry, onNew }) {
  return (
    <div className="bg-[#FBF8EF] rounded-3xl border border-[#D6CFB8] p-8 sm:p-12 text-center shadow-[0_30px_60px_-30px_rgba(0,0,0,0.12)]">
      <div className="w-16 h-16 mx-auto rounded-full bg-[#245A40] grid place-items-center">
        <CheckCircle2 className="w-8 h-8 text-[#F6F2E8]" />
      </div>
      <h2 className="font-display text-3xl sm:text-4xl text-[#1B2A22] mt-6">Thank you!</h2>
      <p className="text-[#3F4A42] mt-3 max-w-lg mx-auto leading-relaxed">
        We’ve received your catering inquiry and our team will get back to you within one business day with a tailored proposal. A confirmation email is on its way to <span className="font-semibold">{inquiry.email}</span>.
      </p>

      <div className="mt-6 inline-flex items-center gap-2 bg-[#E6EBD5] text-[#1B4631] text-xs font-semibold px-3 py-1.5 rounded-full border border-[#CFDAB1]">
        Inquiry ID: <span className="font-mono text-[#245A40]">{inquiry.id.slice(0, 8)}</span>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Button asChild className="btn-primary rounded-full h-11 px-6 font-semibold">
          <Link to="/">Back to home</Link>
        </Button>
        <Button onClick={onNew} variant="ghost" className="btn-ghost rounded-full h-11 px-6 font-semibold border border-[#1B2A22]/15">
          Submit another
        </Button>
      </div>
    </div>
  );
}

function Field({ label, hint, error, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-[#1B2A22] flex items-center gap-1">
        {label} {hint && <span className="text-[10px] font-normal text-[#6F6A55]">{hint}</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
      {error && <div className="text-[11px] text-[#A4471A] mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {error}</div>}
    </div>
  );
}

function InfoLine({ icon: Icon, k, v }) {
  return (
    <li className="flex items-start gap-2.5">
      <Icon className="w-4 h-4 text-[#245A40] mt-0.5 shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[#6F6A55] font-bold">{k}</div>
        <div className="text-[#1B2A22] font-medium">{v}</div>
      </div>
    </li>
  );
}
